import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { apiBaseUrl } from '../../config/runtime';
import './AISettings.css';

const emptyAssistantForm = {
  id: null,
  name: '',
  provider: 'openai',
  assistant_id: '',
  model: '',
  prompt: '',
  openai_api_key: '',
  is_active: false,
  workspace_id: '',
  instance_names: [],
};

const emptyWorkspaceForm = {
  id: null,
  name: '',
  color: '#65202A',
};

const emptyProvisionForm = {
  name: '',
  model: '',
  workspace_id: '',
  set_active: true,
};

const normalizeInstances = (payload) => {
  const list = payload?.instances || [];
  return list
    .map((item) => {
      const instanceName = String(item.instance_name || item.session || item.name || '').trim();
      if (!instanceName) return null;
      const displayName = String(item.display_name || item.name || item.session || instanceName).trim();
      return {
        instance_name: instanceName,
        display_name: displayName,
        name: item.name || displayName,
        status: item.status || 'unknown',
        connected: Boolean(item.connected),
      };
    })
    .filter(Boolean);
};

const AISettings = () => {
  const companyCode = localStorage.getItem('companyCode');
  const headers = useMemo(() => {
    const h = {};
    if (companyCode) h['X-Company-Code'] = companyCode;
    return h;
  }, [companyCode]);

  const [loading, setLoading] = useState(true);
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [savingAssistant, setSavingAssistant] = useState(false);
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [provisioningAssistant, setProvisioningAssistant] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [integrationMeta, setIntegrationMeta] = useState({
    has_api_key: false,
    api_key_masked: null,
    using_default_api_key: false,
    global_assistant_id: null,
    active_assistant_id: null,
    active_assistant_name: null,
  });

  const [runtimeMeta, setRuntimeMeta] = useState({
    assistant_id: null,
    active_assistant_id: null,
    active_assistant_name: null,
    has_api_key: false,
    api_key_masked: null,
    runtime_source: 'global',
    runtime_instance_name: null,
  });

  const [integrationForm, setIntegrationForm] = useState({
    prompt: '',
    openai_api_key: '',
    assistant_id: '',
  });
  const [clearIntegrationApiKey, setClearIntegrationApiKey] = useState(false);
  const [showAdvancedGlobal, setShowAdvancedGlobal] = useState(false);
  const [provisionForm, setProvisionForm] = useState(emptyProvisionForm);

  const [assistants, setAssistants] = useState([]);
  const [assistantForm, setAssistantForm] = useState(emptyAssistantForm);
  const [clearAssistantApiKey, setClearAssistantApiKey] = useState(false);
  const [isEditingAssistant, setIsEditingAssistant] = useState(false);

  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceForm, setWorkspaceForm] = useState(emptyWorkspaceForm);
  const [isEditingWorkspace, setIsEditingWorkspace] = useState(false);
  const [showWorkspaceManager, setShowWorkspaceManager] = useState(false);
  const [selectedWorkspaceFilter, setSelectedWorkspaceFilter] = useState('all');

  const [instances, setInstances] = useState([]);
  const [selectedRuntimeInstance, setSelectedRuntimeInstance] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsSource, setModelsSource] = useState('fallback');

  const loadRuntimePreview = useCallback(async (instanceName = '') => {
    setRuntimeLoading(true);
    try {
      const params = {};
      if (instanceName) params.instance_name = instanceName;
      const response = await axios.get(`${apiBaseUrl}/settings/ai/runtime`, { headers, params });
      const runtimeData = response.data || {};
      setRuntimeMeta({
        assistant_id: runtimeData.assistant_id || null,
        active_assistant_id: runtimeData.active_assistant_id || null,
        active_assistant_name: runtimeData.active_assistant_name || null,
        has_api_key: Boolean(runtimeData.has_api_key),
        api_key_masked: runtimeData.api_key_masked || null,
        runtime_source: runtimeData.runtime_source || 'global',
        runtime_instance_name: runtimeData.runtime_instance_name || null,
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Nao foi possivel carregar o runtime da IA.');
    } finally {
      setRuntimeLoading(false);
    }
  }, [headers]);

  const loadModelCatalog = useCallback(async (apiKeyOverride = '') => {
    setLoadingModels(true);
    try {
      const payload = {};
      if (apiKeyOverride) payload.openai_api_key = apiKeyOverride;
      const response = apiKeyOverride
        ? await axios.post(`${apiBaseUrl}/settings/ai/models`, payload, { headers })
        : await axios.get(`${apiBaseUrl}/settings/ai/models`, { headers });
      const modelData = response.data || {};
      const modelList = Array.isArray(modelData.models) ? modelData.models : [];
      setAvailableModels(modelList);
      setModelsSource(modelData.source || 'fallback');
      setProvisionForm((prev) => {
        const current = String(prev.model || '').trim();
        if (current && modelList.includes(current)) return prev;
        return { ...prev, model: modelData.default_model || modelList[0] || current || 'gpt-5-mini' };
      });
    } catch (err) {
      setAvailableModels([]);
      setModelsSource('fallback');
      setProvisionForm((prev) => ({ ...prev, model: prev.model || 'gpt-5-mini' }));
    } finally {
      setLoadingModels(false);
    }
  }, [headers]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, assistantsRes, workspacesRes, instancesRes, modelsRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/settings/ai`, { headers }),
        axios.get(`${apiBaseUrl}/settings/ai/assistants`, { headers }),
        axios.get(`${apiBaseUrl}/settings/ai/workspaces`, { headers }),
        axios.get(`${apiBaseUrl}/whatsapp/instances`, { headers }),
        axios.get(`${apiBaseUrl}/settings/ai/models`, { headers }),
      ]);

      const settingsData = settingsRes.data || {};
      const assistantsData = Array.isArray(assistantsRes.data) ? assistantsRes.data : [];
      const workspaceData = Array.isArray(workspacesRes.data) ? workspacesRes.data : [];
      const instanceData = normalizeInstances(instancesRes.data || {});
      const modelData = modelsRes.data || {};
      const modelList = Array.isArray(modelData.models) ? modelData.models : [];

      setIntegrationForm({
        prompt: settingsData.prompt || '',
        openai_api_key: '',
        assistant_id: settingsData.assistant_id || '',
      });
      setIntegrationMeta({
        has_api_key: Boolean(settingsData.has_api_key),
        api_key_masked: settingsData.api_key_masked || null,
        using_default_api_key: Boolean(settingsData.using_default_api_key),
        global_assistant_id: settingsData.assistant_id || null,
        active_assistant_id: settingsData.active_assistant_id || null,
        active_assistant_name: settingsData.active_assistant_name || null,
      });
      setAssistants(assistantsData);
      setWorkspaces(workspaceData);
      setInstances(instanceData);
      setAvailableModels(modelList);
      setModelsSource(modelData.source || 'fallback');
      setProvisionForm((prev) => {
        const next = { ...prev };
        if (!prev.workspace_id) {
          const defaultWorkspace = workspaceData[0]?.id;
          if (defaultWorkspace) next.workspace_id = String(defaultWorkspace);
        }
        const modelValue = String(prev.model || '').trim();
        if (!modelValue || (modelList.length > 0 && !modelList.includes(modelValue))) {
          next.model = modelData.default_model || modelList[0] || modelValue || 'gpt-5-mini';
        }
        return next;
      });
      setClearIntegrationApiKey(false);
      setClearAssistantApiKey(false);

      setSelectedWorkspaceFilter((prev) => {
        if (prev === 'all') return prev;
        const stillExists = workspaceData.some((w) => String(w.id) === String(prev));
        return stillExists ? prev : 'all';
      });

      setSelectedRuntimeInstance((prev) => {
        if (!prev) return prev;
        const exists = instanceData.some((inst) => inst.instance_name === prev);
        return exists ? prev : '';
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Nao foi possivel carregar as configuracoes de IA.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadRuntimePreview(selectedRuntimeInstance);
  }, [selectedRuntimeInstance, loadRuntimePreview]);

  const workspaceById = useMemo(() => {
    const map = new Map();
    workspaces.forEach((workspace) => map.set(String(workspace.id), workspace));
    return map;
  }, [workspaces]);

  const assistantsByWorkspace = useMemo(() => {
    const counts = {};
    assistants.forEach((assistant) => {
      const key = String(assistant.workspace_id || 'none');
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [assistants]);

  const filteredAssistants = useMemo(() => {
    if (selectedWorkspaceFilter === 'all') return assistants;
    return assistants.filter((assistant) => String(assistant.workspace_id || '') === String(selectedWorkspaceFilter));
  }, [assistants, selectedWorkspaceFilter]);

  const resetAssistantEditor = useCallback(() => {
    const defaultWorkspace = selectedWorkspaceFilter !== 'all'
      ? String(selectedWorkspaceFilter)
      : String(workspaces[0]?.id || '');
    setAssistantForm({
      ...emptyAssistantForm,
      workspace_id: defaultWorkspace,
      model: availableModels[0] || 'gpt-5-mini',
    });
    setClearAssistantApiKey(false);
    setIsEditingAssistant(false);
  }, [selectedWorkspaceFilter, workspaces, availableModels]);

  const resetWorkspaceEditor = () => {
    setWorkspaceForm(emptyWorkspaceForm);
    setIsEditingWorkspace(false);
  };

  const handleIntegrationChange = (e) => {
    const { name, value } = e.target;
    setIntegrationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssistantChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAssistantForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleAssistantInstance = (instanceName) => {
    setAssistantForm((prev) => {
      const exists = prev.instance_names.includes(instanceName);
      return {
        ...prev,
        instance_names: exists
          ? prev.instance_names.filter((name) => name !== instanceName)
          : [...prev.instance_names, instanceName],
      };
    });
  };

  const handleSaveIntegration = async () => {
    if (!integrationForm.prompt.trim()) {
      setError('O prompt padrao nao pode ficar vazio.');
      setSuccess('');
      return;
    }
    setSavingIntegration(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        prompt: integrationForm.prompt.trim(),
      };
      if (showAdvancedGlobal) {
        payload.assistant_id = integrationForm.assistant_id ? integrationForm.assistant_id.trim() : null;
      }
      if (integrationForm.openai_api_key.trim()) payload.openai_api_key = integrationForm.openai_api_key.trim();
      if (clearIntegrationApiKey) payload.clear_api_key = true;

      await axios.put(`${apiBaseUrl}/settings/ai`, payload, { headers });
      await loadData();
      await loadRuntimePreview(selectedRuntimeInstance);
      setIntegrationForm((prev) => ({ ...prev, openai_api_key: '' }));
      setSuccess('Configuracoes globais de IA atualizadas.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao salvar configuracoes globais de IA.');
    } finally {
      setSavingIntegration(false);
    }
  };

  const handleProvisionAssistant = async () => {
    if (!integrationForm.prompt.trim()) {
      setError('Defina o prompt padrao antes de provisionar o assistente.');
      setSuccess('');
      return;
    }

    setProvisioningAssistant(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: provisionForm.name ? provisionForm.name.trim() : '',
        model: provisionForm.model || availableModels[0] || 'gpt-5-mini',
        prompt: integrationForm.prompt.trim(),
        set_active: Boolean(provisionForm.set_active),
        workspace_id: provisionForm.workspace_id ? Number(provisionForm.workspace_id) : null,
      };
      if (integrationForm.openai_api_key.trim()) {
        payload.openai_api_key = integrationForm.openai_api_key.trim();
      }

      const response = await axios.post(`${apiBaseUrl}/settings/ai/assistants/provision`, payload, { headers });
      await loadData();
      await loadRuntimePreview(selectedRuntimeInstance);
      setIntegrationForm((prev) => ({ ...prev, openai_api_key: '' }));
      setSuccess(`Assistente provisionado com sucesso: ${response?.data?.assistant_id || 'ID não informado'}`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao provisionar assistente automaticamente.');
    } finally {
      setProvisioningAssistant(false);
    }
  };

  const handleStartCreateAssistant = () => {
    setError('');
    setSuccess('');
    resetAssistantEditor();
  };

  const handleStartEditAssistant = (assistant) => {
    setError('');
    setSuccess('');
    setAssistantForm({
      id: assistant.id,
      name: assistant.name || '',
      provider: assistant.provider || 'openai',
      assistant_id: assistant.assistant_id || '',
      model: assistant.model || availableModels[0] || '',
      prompt: assistant.prompt || '',
      openai_api_key: '',
      is_active: Boolean(assistant.is_active),
      workspace_id: String(assistant.workspace_id || ''),
      instance_names: (assistant.instance_bindings || [])
        .map((binding) => String(binding.instance_name || binding.instanceName || '').trim())
        .filter(Boolean),
    });
    setClearAssistantApiKey(false);
    setIsEditingAssistant(true);
  };

  const handleSaveAssistant = async () => {
    if (!assistantForm.name.trim()) {
      setError('Informe o nome do assistente.');
      setSuccess('');
      return;
    }
    if (!assistantForm.assistant_id.trim()) {
      setError('Informe o Assistant ID.');
      setSuccess('');
      return;
    }

    setSavingAssistant(true);
    setError('');
    setSuccess('');
    try {
      const bindings = assistantForm.instance_names.map((instanceName) => {
        const matched = instances.find((inst) => inst.instance_name === instanceName);
        return {
          instance_name: instanceName,
          display_name: matched?.display_name || matched?.name || instanceName,
        };
      });

      const payload = {
        name: assistantForm.name.trim(),
        provider: 'openai',
        assistant_id: assistantForm.assistant_id.trim(),
        model: assistantForm.model ? assistantForm.model.trim() : null,
        prompt: assistantForm.prompt ? assistantForm.prompt.trim() : '',
        is_active: Boolean(assistantForm.is_active),
        workspace_id: assistantForm.workspace_id ? Number(assistantForm.workspace_id) : null,
        instance_bindings: bindings,
      };
      if (assistantForm.openai_api_key.trim()) payload.openai_api_key = assistantForm.openai_api_key.trim();
      if (clearAssistantApiKey) payload.clear_api_key = true;

      if (isEditingAssistant && assistantForm.id) {
        await axios.put(`${apiBaseUrl}/settings/ai/assistants/${assistantForm.id}`, payload, { headers });
        setSuccess('Assistente atualizado com sucesso.');
      } else {
        await axios.post(`${apiBaseUrl}/settings/ai/assistants`, payload, { headers });
        setSuccess('Assistente criado com sucesso.');
      }

      await loadData();
      await loadRuntimePreview(selectedRuntimeInstance);
      resetAssistantEditor();
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao salvar assistente.');
    } finally {
      setSavingAssistant(false);
    }
  };

  const handleActivateAssistant = async (assistantId) => {
    setError('');
    setSuccess('');
    try {
      await axios.post(`${apiBaseUrl}/settings/ai/assistants/${assistantId}/activate`, {}, { headers });
      await loadData();
      await loadRuntimePreview(selectedRuntimeInstance);
      setSuccess('Assistente ativado com sucesso.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao ativar assistente.');
    }
  };

  const handleDeleteAssistant = async (assistantId) => {
    const confirmDelete = window.confirm('Deseja realmente remover este assistente?');
    if (!confirmDelete) return;

    setError('');
    setSuccess('');
    try {
      await axios.delete(`${apiBaseUrl}/settings/ai/assistants/${assistantId}`, { headers });
      await loadData();
      await loadRuntimePreview(selectedRuntimeInstance);
      if (assistantForm.id === assistantId) resetAssistantEditor();
      setSuccess('Assistente removido com sucesso.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao remover assistente.');
    }
  };

  const handleWorkspaceSave = async () => {
    if (!workspaceForm.name.trim()) {
      setError('Informe o nome da area de trabalho.');
      setSuccess('');
      return;
    }
    setSavingWorkspace(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: workspaceForm.name.trim(),
        color: workspaceForm.color || '#65202A',
      };
      if (isEditingWorkspace && workspaceForm.id) {
        await axios.put(`${apiBaseUrl}/settings/ai/workspaces/${workspaceForm.id}`, payload, { headers });
        setSuccess('Area de trabalho atualizada.');
      } else {
        await axios.post(`${apiBaseUrl}/settings/ai/workspaces`, payload, { headers });
        setSuccess('Area de trabalho criada.');
      }
      await loadData();
      resetWorkspaceEditor();
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao salvar area de trabalho.');
    } finally {
      setSavingWorkspace(false);
    }
  };

  const handleWorkspaceEdit = (workspace) => {
    setError('');
    setSuccess('');
    setWorkspaceForm({
      id: workspace.id,
      name: workspace.name || '',
      color: workspace.color || '#65202A',
    });
    setIsEditingWorkspace(true);
    setShowWorkspaceManager(true);
  };

  const handleWorkspaceDelete = async (workspace) => {
    const confirmDelete = window.confirm(`Deseja remover a area "${workspace.name}"?`);
    if (!confirmDelete) return;

    setError('');
    setSuccess('');
    try {
      await axios.delete(`${apiBaseUrl}/settings/ai/workspaces/${workspace.id}`, { headers });
      await loadData();
      if (String(selectedWorkspaceFilter) === String(workspace.id)) {
        setSelectedWorkspaceFilter('all');
      }
      setSuccess('Area de trabalho removida.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Erro ao remover area de trabalho.');
    }
  };

  if (loading) {
    return <div className="ai-settings">Carregando configuracoes de IA...</div>;
  }

  return (
    <div className="ai-settings">
      <div className="ai-settings__header">
        <h3>Gestao de IA</h3>
        <p>Organize assistentes por area de trabalho e vincule cada agente as instancias de WhatsApp.</p>
      </div>

      {error && <div className="ai-settings__error">{error}</div>}
      {success && <div className="ai-settings__success">{success}</div>}

      <section className="ai-settings__runtime-strip">
        <div className="ai-settings__runtime-item">
          <span>Assistente em uso</span>
          <strong>{runtimeMeta.active_assistant_name || 'Fallback Global'}</strong>
        </div>
        <div className="ai-settings__runtime-item">
          <span>Fonte de resolucao</span>
          <strong>{runtimeMeta.runtime_source || 'global'}</strong>
        </div>
        <div className="ai-settings__runtime-item">
          <span>Assistant ID em uso</span>
          <strong>{runtimeMeta.assistant_id || 'Nao definido'}</strong>
        </div>
        <div className="ai-settings__runtime-item">
          <span>API key em uso</span>
          <strong>{runtimeMeta.has_api_key ? (runtimeMeta.api_key_masked || 'Configurada') : 'Nao configurada'}</strong>
        </div>
        <div className="ai-settings__runtime-item ai-settings__runtime-item--selector">
          <span>Preview por instancia</span>
          <select
            value={selectedRuntimeInstance}
            onChange={(e) => setSelectedRuntimeInstance(e.target.value)}
          >
            <option value="">Sem instancia (padrao)</option>
            {instances.map((inst) => (
              <option key={inst.instance_name} value={inst.instance_name}>
                {inst.display_name} ({inst.status})
              </option>
            ))}
          </select>
          <small>
            {runtimeLoading
              ? 'Atualizando runtime...'
              : selectedRuntimeInstance
                ? `Instancia selecionada: ${selectedRuntimeInstance}`
                : 'Mostrando runtime padrao do tenant.'}
          </small>
        </div>
      </section>

      <section className="ai-settings__card">
        <div className="ai-settings__list-header">
          <h4>Areas de Trabalho</h4>
          <div className="ai-settings__list-actions">
            <button className="ghost" onClick={() => setShowWorkspaceManager((prev) => !prev)}>
              {showWorkspaceManager ? 'Fechar Gerenciador' : 'Gerenciar Areas'}
            </button>
            <button className="ghost" onClick={() => {
              resetWorkspaceEditor();
              setShowWorkspaceManager(true);
            }}>
              Nova Area
            </button>
          </div>
        </div>

        <div className="ai-settings__workspace-filter">
          <button
            className={selectedWorkspaceFilter === 'all' ? 'active' : ''}
            onClick={() => setSelectedWorkspaceFilter('all')}
          >
            Todas ({assistants.length})
          </button>
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              className={String(selectedWorkspaceFilter) === String(workspace.id) ? 'active' : ''}
              onClick={() => setSelectedWorkspaceFilter(String(workspace.id))}
            >
              <span className="dot" style={{ backgroundColor: workspace.color || '#65202A' }} />
              {workspace.name} ({workspace.assistant_count ?? assistantsByWorkspace[String(workspace.id)] ?? 0})
            </button>
          ))}
        </div>

        {showWorkspaceManager && (
          <div className="ai-settings__workspace-manager">
            <div className="ai-settings__workspace-form">
              <input
                type="text"
                placeholder="Nome da area"
                value={workspaceForm.name}
                onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <input
                type="color"
                value={workspaceForm.color}
                onChange={(e) => setWorkspaceForm((prev) => ({ ...prev, color: e.target.value }))}
              />
              <button onClick={handleWorkspaceSave} disabled={savingWorkspace}>
                {savingWorkspace ? 'Salvando...' : (isEditingWorkspace ? 'Salvar Area' : 'Criar Area')}
              </button>
              {isEditingWorkspace && (
                <button className="ghost" onClick={resetWorkspaceEditor}>
                  Cancelar
                </button>
              )}
            </div>

            <div className="ai-settings__workspace-list">
              {workspaces.map((workspace) => (
                <article key={workspace.id} className="ai-settings__workspace-item">
                  <div>
                    <strong>{workspace.name}</strong>
                    <small>{workspace.assistant_count ?? assistantsByWorkspace[String(workspace.id)] ?? 0} assistente(s)</small>
                  </div>
                  <div className="ai-settings__workspace-actions">
                    <span className="swatch" style={{ backgroundColor: workspace.color || '#65202A' }} />
                    <button className="ghost" onClick={() => handleWorkspaceEdit(workspace)}>Editar</button>
                    {String(workspace.name || '').toLowerCase() !== 'geral' && (
                      <button className="ghost danger" onClick={() => handleWorkspaceDelete(workspace)}>Excluir</button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="ai-settings__grid">
        <section className="ai-settings__card">
          <h4>Integracao Global (Fallback)</h4>
          <p>Usada quando nao existir assistente vinculado por instancia nem assistente ativo.</p>

          <div className="ai-settings__status">
            <span>
              Chave global atual: {integrationMeta.has_api_key ? (integrationMeta.api_key_masked || 'Configurada') : 'Nao configurada'}
            </span>
            {integrationMeta.using_default_api_key && <small>Usando chave padrao definida no servidor.</small>}
          </div>

          <div className="ai-settings__form">
            <label>
              Prompt padrao
              <textarea
                name="prompt"
                rows="7"
                value={integrationForm.prompt}
                onChange={handleIntegrationChange}
                placeholder="Defina o comportamento padrao da IA."
              />
            </label>

            <label>
              OpenAI API Key global (opcional)
              <input
                type="password"
                name="openai_api_key"
                value={integrationForm.openai_api_key}
                onChange={handleIntegrationChange}
                placeholder="sk-..."
              />
            </label>

            <div className="ai-settings__provision">
              <div className="ai-settings__provision-head">
                <strong>Provisionamento automatico</strong>
                <small>O sistema cria o Assistant ID na OpenAI e ativa localmente.</small>
              </div>
              <div className="ai-settings__provision-grid">
                <label>
                  Nome do assistente
                  <input
                    type="text"
                    value={provisionForm.name}
                    onChange={(e) => setProvisionForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Assistente Padrão"
                  />
                </label>
                <label>
                  Modelo
                  {availableModels.length > 0 ? (
                    <select
                      value={provisionForm.model}
                      onChange={(e) => setProvisionForm((prev) => ({ ...prev, model: e.target.value }))}
                    >
                      {availableModels.map((modelId) => (
                        <option key={modelId} value={modelId}>{modelId}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={provisionForm.model}
                      onChange={(e) => setProvisionForm((prev) => ({ ...prev, model: e.target.value }))}
                      placeholder="gpt-5-mini"
                    />
                  )}
                </label>
                <label>
                  Area de trabalho
                  <select
                    value={provisionForm.workspace_id}
                    onChange={(e) => setProvisionForm((prev) => ({ ...prev, workspace_id: e.target.value }))}
                  >
                    <option value="">Padrao</option>
                    {workspaces.map((workspace) => (
                      <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="ai-settings__checkbox">
                <input
                  type="checkbox"
                  checked={provisionForm.set_active}
                  onChange={(e) => setProvisionForm((prev) => ({ ...prev, set_active: e.target.checked }))}
                />
                Definir este assistente como ativo ao criar
              </label>
              <div className="ai-settings__provision-foot">
                <small>Catalogo de modelos: {modelsSource === 'openai_api' ? 'OpenAI (tempo real)' : 'Fallback local'}</small>
                <button
                  className="ghost"
                  onClick={() => loadModelCatalog(integrationForm.openai_api_key.trim())}
                  disabled={loadingModels}
                >
                  {loadingModels ? 'Atualizando modelos...' : 'Atualizar modelos'}
                </button>
              </div>
              <div className="ai-settings__actions">
                <button onClick={handleProvisionAssistant} disabled={provisioningAssistant}>
                  {provisioningAssistant ? 'Provisionando...' : 'Gerar Assistente Automaticamente'}
                </button>
              </div>
            </div>

            <button className="ghost ai-settings__advanced-toggle" onClick={() => setShowAdvancedGlobal((prev) => !prev)}>
              {showAdvancedGlobal ? 'Ocultar Modo Avancado' : 'Mostrar Modo Avancado'}
            </button>

            {showAdvancedGlobal && (
              <label>
                Assistant ID global (fallback - manual)
                <input
                  type="text"
                  name="assistant_id"
                  value={integrationForm.assistant_id}
                  onChange={handleIntegrationChange}
                  placeholder="asst_..."
                />
              </label>
            )}

            <div className="ai-settings__status">
              <span>Assistant ID global atual: {integrationMeta.global_assistant_id || 'Nao definido'}</span>
            </div>

            <label className="ai-settings__checkbox">
              <input
                type="checkbox"
                checked={clearIntegrationApiKey}
                onChange={(e) => setClearIntegrationApiKey(e.target.checked)}
              />
              Remover chave global salva e usar somente a chave padrao do sistema
            </label>

            <div className="ai-settings__actions">
              <button onClick={handleSaveIntegration} disabled={savingIntegration}>
                {savingIntegration ? 'Salvando...' : 'Salvar Integracao Global'}
              </button>
            </div>
          </div>
        </section>

        <section className="ai-settings__card">
          <div className="ai-settings__list-header">
            <h4>Assistentes Cadastrados</h4>
            <button className="ghost" onClick={handleStartCreateAssistant}>Novo Assistente</button>
          </div>

          {filteredAssistants.length === 0 && (
            <div className="ai-settings__empty">Nenhum assistente nesta area de trabalho.</div>
          )}

          {filteredAssistants.length > 0 && (
            <div className="ai-settings__assistant-list">
              {filteredAssistants.map((assistant) => {
                const workspace = workspaceById.get(String(assistant.workspace_id || ''));
                return (
                  <article key={assistant.id} className={`ai-settings__assistant-item ${assistant.is_active ? 'active' : ''}`}>
                    <div className="ai-settings__assistant-main">
                      <div className="ai-settings__assistant-title">
                        <strong>{assistant.name}</strong>
                        {assistant.is_active && <span>Ativo</span>}
                      </div>
                      <small>Assistant ID: {assistant.assistant_id}</small>
                      <small>Modelo: {assistant.model || 'Nao definido'}</small>
                      <small>Area: {workspace?.name || assistant.workspace_name || 'Sem area'}</small>
                      <small>
                        Prompt override: {assistant.has_prompt_override ? 'Sim' : 'Nao'} | API key override: {assistant.has_api_key ? 'Sim' : 'Nao'}
                      </small>
                      <div className="ai-settings__instance-chips">
                        {(assistant.instance_bindings || []).length === 0 && <span className="chip muted">Sem vinculo de instancia</span>}
                        {(assistant.instance_bindings || []).map((binding) => (
                          <span key={`${assistant.id}-${binding.instance_name}`} className="chip">
                            {binding.display_name || binding.instance_name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="ai-settings__assistant-actions">
                      {!assistant.is_active && (
                        <button onClick={() => handleActivateAssistant(assistant.id)}>Ativar</button>
                      )}
                      <button className="ghost" onClick={() => handleStartEditAssistant(assistant)}>Editar</button>
                      <button className="ghost danger" onClick={() => handleDeleteAssistant(assistant.id)}>Excluir</button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <section className="ai-settings__card">
        <div className="ai-settings__list-header">
          <h4>{isEditingAssistant ? 'Editar Assistente' : 'Novo Assistente'}</h4>
          {isEditingAssistant && (
            <button className="ghost" onClick={resetAssistantEditor}>Cancelar Edicao</button>
          )}
        </div>

        <div className="ai-settings__form ai-settings__assistant-form-grid">
          <label>
            Nome
            <input
              type="text"
              name="name"
              value={assistantForm.name}
              onChange={handleAssistantChange}
              placeholder="Ex: Suporte N1"
            />
          </label>

          <label>
            Assistant ID
            <input
              type="text"
              name="assistant_id"
              value={assistantForm.assistant_id}
              onChange={handleAssistantChange}
              placeholder="asst_..."
            />
          </label>

          <label>
            Modelo
            {availableModels.length > 0 ? (
              <select
                name="model"
                value={assistantForm.model}
                onChange={handleAssistantChange}
              >
                <option value="">Selecionar</option>
                {availableModels.map((modelId) => (
                  <option key={modelId} value={modelId}>{modelId}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="model"
                value={assistantForm.model}
                onChange={handleAssistantChange}
                placeholder="gpt-5-mini"
              />
            )}
          </label>

          <label>
            Area de trabalho
            <select
              name="workspace_id"
              value={assistantForm.workspace_id}
              onChange={handleAssistantChange}
            >
              <option value="">Selecionar</option>
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
              ))}
            </select>
          </label>

          <label>
            OpenAI API Key override (opcional)
            <input
              type="password"
              name="openai_api_key"
              value={assistantForm.openai_api_key}
              onChange={handleAssistantChange}
              placeholder="sk-..."
            />
          </label>

          <label className="ai-settings__full-row">
            Prompt override (opcional)
            <textarea
              name="prompt"
              rows="5"
              value={assistantForm.prompt}
              onChange={handleAssistantChange}
              placeholder="Se vazio, usa o prompt global."
            />
          </label>

          <div className="ai-settings__full-row ai-settings__instance-picker">
            <h5>Instancias de WhatsApp vinculadas ao assistente</h5>
            <p>Quando uma mensagem entrar por uma instancia vinculada, este assistente sera usado.</p>
            <div className="ai-settings__instance-grid">
              {instances.length === 0 && <div className="ai-settings__empty">Nenhuma instancia cadastrada.</div>}
              {instances.map((inst) => {
                const checked = assistantForm.instance_names.includes(inst.instance_name);
                return (
                  <label key={inst.instance_name} className={`ai-settings__instance-item ${checked ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAssistantInstance(inst.instance_name)}
                    />
                    <div>
                      <strong>{inst.display_name}</strong>
                      <small>{inst.instance_name}</small>
                      <small className={`status ${inst.connected ? 'ok' : 'off'}`}>
                        {inst.connected ? 'Conectada' : 'Desconectada'} - {inst.status}
                      </small>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <label className="ai-settings__checkbox ai-settings__full-row">
            <input
              type="checkbox"
              name="is_active"
              checked={assistantForm.is_active}
              onChange={handleAssistantChange}
            />
            Definir este assistente como ativo apos salvar
          </label>

          {isEditingAssistant && (
            <label className="ai-settings__checkbox ai-settings__full-row">
              <input
                type="checkbox"
                checked={clearAssistantApiKey}
                onChange={(e) => setClearAssistantApiKey(e.target.checked)}
              />
              Remover API key override deste assistente
            </label>
          )}

          <div className="ai-settings__actions ai-settings__full-row">
            <button onClick={handleSaveAssistant} disabled={savingAssistant}>
              {savingAssistant ? 'Salvando...' : (isEditingAssistant ? 'Salvar Assistente' : 'Criar Assistente')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISettings;
